import type { LocalSqliteRuntime } from '@src/modules/shared/local-sqlite/domain/LocalSqliteRuntime.ts';
import type {
  ApplyRemoteMasterDataJsonFn,
  CatalogDownloadPort,
  CatalogDownloadProgress,
  CatalogDownloadRunOptions,
  MasterDataApplyCounts,
} from '../domain/ports/CatalogDownloadPort.ts';
import { ok, fail, type Result } from '@core/domain/utils/Result.ts';

export class CatalogDownloadApplicationService implements CatalogDownloadPort {
  /** Constructs the service. */
  constructor(
    private readonly localSqlite: LocalSqliteRuntime | undefined,
    private readonly resolveUrl: () => string | null,
    private readonly fetchBundle: (url: string, token: string | null, onProgress?: (loaded: number, total: number) => void) => Promise<unknown>,
    private readonly applyRemoteMasterDataJson: ApplyRemoteMasterDataJsonFn,
    private readonly assertReachableBeforeFetch: (url: string) => Promise<void> = async () => {},
    private readonly resolveToken: () => string | null = () => null,
  ) {}

  resolveDownloadUrl(): string | null {
    return this.resolveUrl();
  }

  async runDownload(
    onProgress?: (progress: CatalogDownloadProgress) => void,
    options?: CatalogDownloadRunOptions,
  ): Promise<Result<MasterDataApplyCounts, string>> {
    const url = this.resolveUrl();
    if (!url) {
      return fail('Falta configurar la descarga de catálogos: defina SYNC_DOWNLOAD_BASE_URL y SYNC_DOWNLOAD_PATH en el entorno de compilación.');
    }
    if (!this.localSqlite?.getDb()) {
      return fail('No hay base de datos local abierta; esta acción solo aplica en modo SQLite embebido.');
    }
    try {
      await this.assertReachableBeforeFetch(url);
      const token = this.resolveToken();
      const raw = await this.fetchBundle(url, token, (loaded, total) => {
        onProgress?.({ phase: 'downloading', loaded, total });
      });
      const counts = await this.applyRemoteMasterDataJson(raw, {
        ...options,
        onApplyProgress: (loaded, total) => {
          onProgress?.({ phase: 'applying', loaded, total });
        },
      });
      this.localSqlite?.notifyPersist();
      await this.localSqlite?.flushPersist();
      return ok(counts);
    } catch (e) {
      let message = e instanceof Error ? e.message : String(e);
      if (message === 'Failed to fetch' || message === 'TimeoutError') {
        message = 'Fallo de conexión a internet durante la descarga. Verifique su red y vuelva a intentarlo.';
      } else if (message.includes('HTTP 413')) {
        message = 'El tamaño de los datos solicitados es demasiado grande para el servidor (HTTP 413).';
      } else if (message.includes('HTTP 401')) {
        message =
          'No autorizado (HTTP 401). Defina SYNC_DOWNLOAD_BEARER_TOKEN en la compilación o inicie sesión si el servidor exige el mismo token que la API.';
      } else if (message.startsWith('HTTP ')) {
        message = `Fallo del servidor (${message}). Intente más tarde o contacte a soporte.`;
      }
      return fail(
        message ||
        'No se pudo aplicar la actualización. Compruebe la red, el formato JSON del servidor y los datos referenciados (barrios, grupos de alimentos, roles).'
      );
    }
  }
}
